import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaBell, FaEnvelope, FaShieldAlt, FaTrash, FaExclamationTriangle } from 'react-icons/fa';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    courseUpdates: true,
    newLessons: true,
    promotions: false,
  });
  
  // Email preferences
  const [emailPreferences, setEmailPreferences] = useState({
    weeklyDigest: true,
    courseCompletionCertificate: true,
    accountNotifications: true,
  });
  
  // Privacy settings
  const [privacySettings, setPrivacySettings] = useState({
    showProfileProgress: true,
    showEnrolledCourses: true,
    allowDataCollection: true,
  });
  
  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  
  // Handle notification settings change
  const handleNotificationChange = (e) => {
    setNotificationSettings({
      ...notificationSettings,
      [e.target.name]: e.target.checked,
    });
  };
  
  // Handle email preferences change
  const handleEmailPreferenceChange = (e) => {
    setEmailPreferences({
      ...emailPreferences,
      [e.target.name]: e.target.checked,
    });
  };
  
  // Handle privacy settings change
  const handlePrivacyChange = (e) => {
    setPrivacySettings({
      ...privacySettings,
      [e.target.name]: e.target.checked,
    });
  };
  
  // Handle save settings
  const handleSaveSettings = () => {
    // In a real app, this would send the settings to the server
    toast.success('Settings saved successfully');
  };
  
  // Handle delete account
  const handleDeleteAccount = () => {
    if (deleteConfirmation !== user.email) {
      toast.error('Email confirmation does not match');
      return;
    }
    
    // In a real app, this would send a request to delete the account
    toast.info('Account deletion request submitted');
    setShowDeleteModal(false);
  };
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      {/* Notification Settings */}
      <div className="bg-white container-rounded-lg shadow overflow-hidden mb-6">
        <div className="p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center">
            <FaBell className="mr-2 text-primary" />
            Notification Settings
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Email Notifications</h3>
                <p className="text-sm text-gray-500">Receive notifications via email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="emailNotifications" 
                  className="sr-only peer" 
                  checked={notificationSettings.emailNotifications}
                  onChange={handleNotificationChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between pl-6">
              <div>
                <h3 className="font-medium">Course Updates</h3>
                <p className="text-sm text-gray-500">Notifications about updates to your enrolled courses</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="courseUpdates" 
                  className="sr-only peer" 
                  checked={notificationSettings.courseUpdates}
                  onChange={handleNotificationChange}
                  disabled={!notificationSettings.emailNotifications}
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${notificationSettings.emailNotifications ? 'peer-checked:bg-primary' : 'opacity-50'}`}></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between pl-6">
              <div>
                <h3 className="font-medium">New Lessons</h3>
                <p className="text-sm text-gray-500">Notifications when new lessons are available</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="newLessons" 
                  className="sr-only peer" 
                  checked={notificationSettings.newLessons}
                  onChange={handleNotificationChange}
                  disabled={!notificationSettings.emailNotifications}
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${notificationSettings.emailNotifications ? 'peer-checked:bg-primary' : 'opacity-50'}`}></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between pl-6">
              <div>
                <h3 className="font-medium">Promotions and Offers</h3>
                <p className="text-sm text-gray-500">Receive promotional emails and special offers</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="promotions" 
                  className="sr-only peer" 
                  checked={notificationSettings.promotions}
                  onChange={handleNotificationChange}
                  disabled={!notificationSettings.emailNotifications}
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${notificationSettings.emailNotifications ? 'peer-checked:bg-primary' : 'opacity-50'}`}></div>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Email Preferences */}
      <div className="bg-white container-rounded-lg shadow overflow-hidden mb-6">
        <div className="p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center">
            <FaEnvelope className="mr-2 text-primary" />
            Email Preferences
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Weekly Digest</h3>
                <p className="text-sm text-gray-500">Receive a weekly summary of your course progress</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="weeklyDigest" 
                  className="sr-only peer" 
                  checked={emailPreferences.weeklyDigest}
                  onChange={handleEmailPreferenceChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Course Completion Certificate</h3>
                <p className="text-sm text-gray-500">Receive certificates via email when you complete a course</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="courseCompletionCertificate" 
                  className="sr-only peer" 
                  checked={emailPreferences.courseCompletionCertificate}
                  onChange={handleEmailPreferenceChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Account Notifications</h3>
                <p className="text-sm text-gray-500">Receive emails about your account security and updates</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="accountNotifications" 
                  className="sr-only peer" 
                  checked={emailPreferences.accountNotifications}
                  onChange={handleEmailPreferenceChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Privacy Settings */}
      <div className="bg-white container-rounded-lg shadow overflow-hidden mb-6">
        <div className="p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center">
            <FaShieldAlt className="mr-2 text-primary" />
            Privacy Settings
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Show Profile Progress</h3>
                <p className="text-sm text-gray-500">Allow other users to see your course progress</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="showProfileProgress" 
                  className="sr-only peer" 
                  checked={privacySettings.showProfileProgress}
                  onChange={handlePrivacyChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Show Enrolled Courses</h3>
                <p className="text-sm text-gray-500">Allow other users to see courses you're enrolled in</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="showEnrolledCourses" 
                  className="sr-only peer" 
                  checked={privacySettings.showEnrolledCourses}
                  onChange={handlePrivacyChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Data Collection</h3>
                <p className="text-sm text-gray-500">Allow us to collect usage data to improve your experience</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="allowDataCollection" 
                  className="sr-only peer" 
                  checked={privacySettings.allowDataCollection}
                  onChange={handlePrivacyChange}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Save Settings Button */}
      <div className="flex justify-end mb-6">
        <button 
          onClick={handleSaveSettings}
          className="btn-primary"
        >
          Save Settings
        </button>
      </div>
      
      {/* Delete Account */}
      <div className="bg-white container-rounded-lg shadow overflow-hidden">
        <div className="p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center text-red-600">
            <FaTrash className="mr-2" />
            Delete Account
          </h2>
          
          <p className="text-gray-600 mb-4">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          
          <button 
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 container-rounded-lg transition duration-200"
          >
            Delete Account
          </button>
        </div>
      </div>
      
      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
            
            <div className="inline-block align-bottom bg-white container-rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Account
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete your account? All of your data will be permanently removed. This action cannot be undone.
                      </p>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Please type your email to confirm: <span className="font-bold">{user.email}</span>
                        </label>
                        <input 
                          type="email" 
                          className="form-input w-full"
                          value={deleteConfirmation}
                          onChange={(e) => setDeleteConfirmation(e.target.value)}
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className="w-full inline-flex justify-center container-rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </button>
                <button 
                  type="button" 
                  className="mt-3 w-full inline-flex justify-center container-rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;